import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AgentLayout } from "@/components/AgentLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { SUPPLIER_CATEGORIES, CATEGORY_BY_VALUE } from "@/lib/supplierCategories";
import {
  CircleNotch, MagnifyingGlass, ArrowSquareOut, Star, BookOpen, Storefront,
  DownloadSimple, UploadSimple, FileText, CheckCircle, WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function SupplierCard({ supplier, isAdmin }) {
  const cat = CATEGORY_BY_VALUE[supplier.category];
  const CatIcon = cat?.Icon || Storefront;
  const [imgOk, setImgOk] = useState(true);
  const portalDomain = (() => {
    try { return new URL(supplier.portal_url).hostname.replace("www.", ""); } catch { return supplier.portal_url; }
  })();

  return (
    <article
      className="group relative rounded-xl border border-white/10 bg-ink-surface hover:border-[#D4AF37]/40 transition-colors overflow-hidden flex flex-col"
      data-testid={`supplier-card-${supplier.id}`}
    >
      {supplier.preferred && (
        <span
          className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border border-[#D4AF37]/50 bg-[#D4AF37]/15 text-[#D4AF37] font-sans"
          data-testid={`supplier-preferred-${supplier.id}`}
        >
          <Star size={11} weight="fill" /> Preferred
        </span>
      )}

      {/* Logo */}
      <div className="aspect-[16/9] bg-black/40 border-b border-white/5 flex items-center justify-center p-6">
        {supplier.logo_url && imgOk ? (
          <img
            src={supplier.logo_url}
            alt={supplier.name}
            className="max-h-full max-w-full object-contain"
            loading="lazy"
            onError={() => setImgOk(false)}
            data-testid={`supplier-logo-${supplier.id}`}
          />
        ) : (
          <div className="text-center">
            <CatIcon size={40} weight="duotone" className="text-[#D4AF37] mx-auto mb-2" />
            <p className="font-serif text-2xl text-white/80">{supplier.name}</p>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2">
          <CatIcon size={12} weight="duotone" className="text-[#D4AF37]" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/90 font-sans">{cat?.label || supplier.category}</span>
        </div>
        <h3 className="font-serif text-2xl text-white tracking-tight mb-1">{supplier.name}</h3>
        {supplier.description && (
          <p className="text-sm text-white/60 font-sans mb-4 line-clamp-2">{supplier.description}</p>
        )}

        <div className="mt-auto pt-4 space-y-2">
          <a
            href={supplier.portal_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between gap-2 w-full px-4 h-11 rounded-md bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-[0.25em] font-sans font-medium transition-colors"
            data-testid={`supplier-portal-${supplier.id}`}
          >
            <span className="truncate">Go to {portalDomain || "portal"}</span>
            <ArrowSquareOut size={14} weight="duotone" />
          </a>
          {supplier.training_url && (
            <a
              href={supplier.training_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between gap-2 w-full px-4 h-9 rounded-md border border-white/15 hover:border-[#D4AF37]/40 text-white/80 hover:text-[#D4AF37] text-[11px] uppercase tracking-[0.25em] font-sans transition-colors"
              data-testid={`supplier-training-${supplier.id}`}
            >
              <span className="flex items-center gap-1.5"><BookOpen size={12} weight="duotone" /> Sign In / Request Access</span>
              <ArrowSquareOut size={12} weight="duotone" />
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

export default function Suppliers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const loadSuppliers = async () => {
    try {
      const { data } = await api.get("/suppliers");
      setData(data);
    } catch {
      setData({ error: true });
    }
  };

  useEffect(() => { loadSuppliers(); }, []);

  // Triggers a browser download via a fetch+blob so auth cookie + Bearer header travel along.
  const downloadCsv = async (url, filename) => {
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("btt_token") : null;
      const resp = await fetch(`${API}${url}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (err) {
      toast.error(`Download failed: ${err.message || err}`);
    }
  };

  const onExport = () => {
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv("/admin/suppliers/export", `suppliers-${today}.csv`);
  };

  const onDownloadTemplate = () => {
    downloadCsv("/admin/suppliers/template", "suppliers-template.csv");
  };

  const onImportClick = () => fileInputRef.current?.click();

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data: res } = await api.post("/admin/suppliers/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(res);
      if (res.imported > 0) toast.success(res.message);
      else toast.warning(res.message);
      await loadSuppliers();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Import failed";
      toast.error(typeof msg === "string" ? msg : "Import failed");
    } finally {
      setImporting(false);
      // Reset input so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const filtered = useMemo(() => {
    if (!data?.rows) return [];
    const q = search.trim().toLowerCase();
    return data.rows.filter((s) => {
      if (activeCat !== "all" && s.category !== activeCat) return false;
      if (!q) return true;
      const hay = `${s.name} ${s.description} ${CATEGORY_BY_VALUE[s.category]?.label || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [data, search, activeCat]);

  // Per-category counts for chip display
  const counts = useMemo(() => {
    const out = { all: 0 };
    SUPPLIER_CATEGORIES.forEach((c) => { out[c.value] = 0; });
    (data?.rows || []).forEach((s) => {
      out.all += 1;
      if (out[s.category] != null) out[s.category] += 1;
    });
    return out;
  }, [data]);

  if (data === null) {
    return (
      <AgentLayout>
        <div className="py-32 flex justify-center"><CircleNotch size={32} className="text-[#D4AF37] animate-spin" /></div>
      </AgentLayout>
    );
  }
  if (data?.error) {
    return (
      <AgentLayout>
        <div className="border border-dashed border-red-400/30 rounded-lg p-12 text-center text-red-300/80 font-sans">
          Could not load suppliers.
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="mb-10 animate-fade-up flex flex-col md:flex-row md:items-end md:justify-between gap-6" data-testid="suppliers-page">
        <div>
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-3 font-sans">Directory</p>
          <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-white mb-3">
            Our <span className="italic text-[#D4AF37]">suppliers</span>
          </h1>
          <p className="text-white/60 text-sm max-w-xl font-sans">
            Quick links to every preferred supplier portal, training resource, and booking system. Click through to log in directly with your existing credentials.
          </p>
        </div>

        {isAdmin && (
          <div className="flex flex-wrap gap-2 shrink-0" data-testid="suppliers-admin-toolbar">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={onImportFile}
              className="hidden"
              data-testid="suppliers-import-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={onDownloadTemplate}
              className="h-11 px-4 border-white/15 bg-transparent text-white/80 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] text-[11px] uppercase tracking-[0.25em] font-sans"
              data-testid="suppliers-template-btn"
            >
              <FileText size={16} weight="duotone" className="mr-2" /> Template
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onImportClick}
              disabled={importing}
              className="h-11 px-4 border-white/15 bg-transparent text-white/80 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] text-[11px] uppercase tracking-[0.25em] font-sans"
              data-testid="suppliers-import-btn"
            >
              {importing ? (
                <CircleNotch size={16} className="mr-2 animate-spin" />
              ) : (
                <UploadSimple size={16} weight="duotone" className="mr-2" />
              )}
              {importing ? "Importing…" : "Import CSV"}
            </Button>
            <Button
              type="button"
              onClick={onExport}
              className="h-11 px-4 bg-[#D4AF37] hover:bg-[#F3C853] text-black text-[11px] uppercase tracking-[0.25em] font-sans"
              data-testid="suppliers-export-btn"
            >
              <DownloadSimple size={16} weight="duotone" className="mr-2" /> Export CSV
            </Button>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-5 max-w-xl">
        <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" weight="duotone" />
        <Input
          placeholder="Search supplier name or description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-11 h-12 bg-black/40 border-white/10 text-white focus:border-[#D4AF37]"
          data-testid="suppliers-search"
        />
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="suppliers-categories">
        <CategoryChip active={activeCat === "all"} onClick={() => setActiveCat("all")} label={`All (${counts.all || 0})`} testId="cat-chip-all" />
        {SUPPLIER_CATEGORIES.map((c) => {
          const Icon = c.Icon;
          return (
            <CategoryChip
              key={c.value}
              active={activeCat === c.value}
              onClick={() => setActiveCat(c.value)}
              icon={<Icon size={14} weight="duotone" className="mr-1.5" />}
              label={`${c.label} (${counts[c.value] || 0})`}
              testId={`cat-chip-${c.value}`}
            />
          );
        })}
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-lg p-16 text-center font-sans" data-testid="suppliers-empty">
          <Storefront size={32} weight="duotone" className="text-white/30 mx-auto mb-3" />
          <p className="text-white/60 text-sm">
            {(data.rows || []).length === 0
              ? "No suppliers in the directory yet — check back soon."
              : "No suppliers match the current search & filters."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="suppliers-grid">
          {filtered.map((s) => <SupplierCard key={s.id} supplier={s} isAdmin={isAdmin} />)}
        </div>
      )}

      <p className="text-[11px] text-white/40 italic mt-8">
        Best Time Travel & Tours never auto-logs you into supplier systems. Use the credentials provided by each supplier when you click through.
      </p>

      {/* Import result dialog — shows summary + per-row errors after a CSV import */}
      <Dialog open={!!importResult} onOpenChange={(o) => !o && setImportResult(null)}>
        <DialogContent className="bg-ink-surface border-white/10 text-white max-w-lg" data-testid="suppliers-import-result">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl flex items-center gap-2">
              {importResult?.imported > 0 ? (
                <CheckCircle size={22} weight="duotone" className="text-emerald-300" />
              ) : (
                <WarningCircle size={22} weight="duotone" className="text-amber-300" />
              )}
              Import complete
            </DialogTitle>
            <DialogDescription className="text-white/60 font-sans text-xs">
              {importResult?.message || ""}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 text-sm font-sans">
            <div className="flex items-center justify-between rounded-md bg-black/30 border border-white/10 px-3 py-2">
              <span className="text-white/70">Imported</span>
              <span className="text-emerald-300 font-mono">{importResult?.imported ?? 0}</span>
            </div>
            <div className="flex items-center justify-between rounded-md bg-black/30 border border-white/10 px-3 py-2">
              <span className="text-white/70">Skipped (duplicates)</span>
              <span className="text-white/60 font-mono">{importResult?.skipped ?? 0}</span>
            </div>
            {importResult?.total_errors > 0 && (
              <div className="flex items-center justify-between rounded-md bg-amber-500/10 border border-amber-400/30 px-3 py-2">
                <span className="text-amber-200">Error rows</span>
                <span className="text-amber-200 font-mono">{importResult.total_errors}</span>
              </div>
            )}
          </div>

          {importResult?.errors?.length > 0 && (
            <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-white/10 bg-black/30 p-2 text-[12px] font-sans">
              {importResult.errors.slice(0, 20).map((e, i) => (
                <div key={i} className="text-white/70 py-1 border-b border-white/5 last:border-0">
                  <span className="text-white/40">row {e.row}</span>
                  {e.name && <span className="text-white/60"> · {e.name}</span>}
                  <span className="text-amber-300 block">{e.error}</span>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={() => setImportResult(null)}
              className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-10 px-5 text-xs uppercase tracking-widest"
              data-testid="suppliers-import-result-close"
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}

function CategoryChip({ active, onClick, label, icon, testId }) {
  return (
    <Button
      type="button"
      onClick={onClick}
      variant="ghost"
      className={`h-9 px-3 rounded-full border text-xs uppercase tracking-widest font-sans transition-colors ${
        active
          ? "bg-[#D4AF37] border-[#D4AF37] text-black hover:bg-[#F3C853]"
          : "bg-transparent border-white/15 text-white/70 hover:border-[#D4AF37]/40 hover:text-[#D4AF37]"
      }`}
      data-testid={testId}
    >
      {icon}{label}
    </Button>
  );
}
